import { USERS_ACCESS_ROLE_LABEL_KEYS } from '../../constants/usersAccess'
import { useUsersAccessInvite } from '../../hooks/useUsersAccessInvite'
import type { UsersAccessRole } from '../../types/usersAccess'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import {
  Checkbox,
  Modal,
  Stack,
  Text,
  TextInput,
  errorColorFor,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import type { Role, RoleExtended } from '../../types/types'
import { RoleCheckboxOption } from './RoleCheckboxOption'

const INVITABLE_ROLES: UsersAccessRole[] = [
  'IMPLEMENTER',
  'PROGRAM_MANAGER',
  'OPERATOR',
  'GUARD',
  'READ_ONLY_USER',
  'EMPLOYEE',
]

interface InviteTeamMemberModalIProps {
  colorScheme: ColorScheme
  isOpen: boolean
  onClose: () => void
}

export const InviteTeamMemberModal = ({
  colorScheme,
  isOpen,
  onClose,
}: InviteTeamMemberModalIProps) => {
  const { t } = useTranslation()
  const { hasCRUDPermission } = usePermissions()
  const { errors, isLoading, reset, setField, submit, toggleRole, values } =
    useUsersAccessInvite(colorScheme)

  // ENG-33697: roles the viewer may not grant (Implementer, for PMs) stay
  // visible but disabled, matching the legacy surface where a PM cannot act
  // on Implementers at all
  const isRoleRestricted = (role: UsersAccessRole) =>
    !hasCRUDPermission(role as Role | RoleExtended)

  const close = () => {
    if (isLoading) return
    reset()
    onClose()
  }

  const save = async () => {
    const didSave = await submit()
    if (didSave) onClose()
  }

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      closeOnEsc={!isLoading}
      closeOnOverlayClick={!isLoading}
      colorScheme={colorScheme}
      isCloseHidden={isLoading}
      isOpen={isOpen}
      isSaveDisabled={isLoading}
      isSaveLoading={isLoading}
      maxW='600px'
      onClose={close}
      onSave={save}
      saveButtonLabel={t('settings:rebrand_users_invite_submit')}
      showCancelButton={!isLoading}
      size='compact'
      title={t('settings:rebrand_users_invite_title')}
    >
      <Stack gap='16px'>
        <TextInput
          colorScheme={colorScheme}
          data-testid='InviteTeamMemberModal:firstName'
          hasWarning={Boolean(errors.firstName)}
          inputProps={{ autoComplete: 'given-name' }}
          label={t('settings:rebrand_users_first_name_label')}
          onValueChange={(value) => setField('firstName', value)}
          placeholder={t('settings:rebrand_users_first_name_placeholder')}
          value={values.firstName}
          warningText={errors.firstName}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='InviteTeamMemberModal:lastName'
          hasWarning={Boolean(errors.lastName)}
          inputProps={{ autoComplete: 'family-name' }}
          label={t('settings:rebrand_users_last_name_label')}
          onValueChange={(value) => setField('lastName', value)}
          placeholder={t('settings:rebrand_users_last_name_placeholder')}
          value={values.lastName}
          warningText={errors.lastName}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='InviteTeamMemberModal:email'
          hasWarning={Boolean(errors.email)}
          inputProps={{ autoComplete: 'email', type: 'email' }}
          label={t('settings:rebrand_users_email_label')}
          onValueChange={(value) => setField('email', value)}
          placeholder={t('settings:rebrand_users_email_placeholder')}
          value={values.email}
          warningText={errors.email}
          width='100%'
        />
        <Stack gap='8px'>
          <Text textStyle='caption-medium'>
            {t('settings:rebrand_users_roles_label')}
          </Text>
          {INVITABLE_ROLES.map((role) => (
            <RoleCheckboxOption
              colorScheme={colorScheme}
              isChecked={values.roles.includes(role)}
              isRestricted={isRoleRestricted(role)}
              key={role}
              label={t(USERS_ACCESS_ROLE_LABEL_KEYS[role])}
              onChange={() => toggleRole(role)}
              restrictedLabel={t('settings:rebrand_users_role_restricted_hint')}
              role={role}
              testIdPrefix='InviteTeamMemberModal'
            />
          ))}
          {errors.roles && (
            <Text
              color={errorColorFor(colorScheme)}
              role='alert'
              textStyle='caption'
            >
              {errors.roles}
            </Text>
          )}
        </Stack>
        <Checkbox
          checked={values.isSsoUser}
          colorScheme={colorScheme}
          data-testid='InviteTeamMemberModal:isSsoUser'
          label={t('settings:rebrand_users_sso_label')}
          onChange={(event) => setField('isSsoUser', event.target.checked)}
        />
      </Stack>
    </Modal>
  )
}
